import { Fragment, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  getDevisASigner,
  getDevisParToken,
  ouvrirSignatureDevis,
  signerDevis,
  telechargerPdfDevis,
  urlPdfDevis,
} from '../services/devisService';
import { deposerDocumentParToken, getDocumentsParToken } from '../services/documentClientService';
import { formatDate, formatDateTime, formatMontant } from './devisUtils';
import SelecteurSignature from './SelecteurSignature';
import SignaturePad from './SignaturePad';
import EnteteSignature, { ApercuDocument } from './EnteteSignature';
import '../assets/styles/DemandeDevis.css';
import '../assets/styles/DevisPanel.css';
import '../assets/styles/Clients.css';
import './Signature.css';

/**
 * Champs d'identité que le client peut compléter lui-même avant de signer.
 * La raison sociale et le nom du contact en sont exclus : ils identifient le
 * devis déjà émis (le serveur applique la même liste, cf. devisService).
 */
const CHAMPS_CLIENT = [
  { cle: 'siret', label: 'SIRET', placeholder: '14 chiffres', inputMode: 'numeric' },
  { cle: 'activite', label: "Secteur d'activité", placeholder: 'Ex. : action sociale' },
  { cle: 'adresse', label: 'Adresse', placeholder: 'Numéro et rue', pleineLargeur: true },
  { cle: 'code_postal', label: 'Code postal', placeholder: '69000', inputMode: 'numeric' },
  { cle: 'ville', label: 'Ville', placeholder: 'Lyon' },
];

const CHAMPS_CLIENT_VIDES = Object.fromEntries(CHAMPS_CLIENT.map((c) => [c.cle, '']));

/** Colonnes du sélecteur de devis à faire signer (cf. SelecteurSignature). */
const COLONNES_DEVIS = [
  {
    cle: 'numero',
    label: 'NUMÉRO',
    rendu: (d) => (
      <>
        <div className="devis-numero">{d.numero}</div>
        <div className="devis-contact-email">émis le {formatDate(d.date_emission)}</div>
      </>
    ),
  },
  {
    cle: 'client',
    label: 'CLIENT',
    rendu: (d) => (
      <>
        <div className="devis-contact-name">{d.client_societe || '—'}</div>
        <div className="devis-contact-email">
          {[d.prenom, d.nom].filter(Boolean).join(' ')} · {d.contact_email}
        </div>
      </>
    ),
  },
  {
    cle: 'montant',
    label: 'MONTANT',
    rendu: (d) => (
      <>
        <div className="devis-montant-ht">{formatMontant(d.montant_ht)} HT</div>
        <div className="devis-contact-email">{formatMontant(d.montant_ttc)} TTC</div>
      </>
    ),
  },
  {
    cle: 'signature',
    label: 'SIGNATURE',
    rendu: (d) =>
      d.signature_le ? (
        <>
          <span className="devis-status-pill devis-pill-valide">Signé</span>
          <div className="devis-status-detail">le {formatDate(d.signature_le)}</div>
        </>
      ) : (
        <>
          <span className="devis-status-pill devis-pill-attente">À signer</span>
          {/* La validité échue n'interdit pas de signer : le back-office
              rouvre la signature en connaissance de cause. */}
          <div className="devis-status-detail">
            {d.validite_depassee
              ? `validité échue le ${formatDate(d.date_validite)}`
              : `valable jusqu'au ${formatDate(d.date_validite)}`}
          </div>
        </>
      ),
  },
];

/**
 * Page accessible depuis la barre latérale et depuis le lien reçu par
 * email pour signer un devis.
 *
 * Sans jeton, on est en interne : la page présente les devis validés et
 * ouvre celui qu'un client présent doit signer, sans passer par l'email.
 * Avec jeton, c'est la page de signature elle-même — celle du lien reçu par
 * email, et celle qu'on tend au client sur place : le devis d'abord, tel
 * qu'il partira, puis trois onglets — compléter ses informations, signer,
 * déposer les pièces demandées.
 */
function Signature() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  // Marqué à l'ouverture depuis le sélecteur : c'est ce qui distingue le
  // client venu par email (aucun retour à proposer) de l'opérateur qui a
  // tendu son écran et doit pouvoir revenir à la liste.
  const retourListe = state?.retourListe || null;
  const [devis, setDevis] = useState(null);
  const [loading, setLoading] = useState(Boolean(token));
  const [error, setError] = useState(null);
  const [validation, setValidation] = useState(false);
  const [erreurSignature, setErreurSignature] = useState(null);
  const [telechargement, setTelechargement] = useState(false);
  // Informations d'entreprise que le client complète avant de signer : le
  // devis part fréquemment avec un SIRET ou une adresse « à communiquer ».
  const [infos, setInfos] = useState(CHAMPS_CLIENT_VIDES);
  // Pièces à apporter pour les formations du devis : attendues, déposées.
  // Jamais bloquantes — le client signe puis complète, dans l'ordre qu'il veut.
  const [documents, setDocuments] = useState(null);
  const [depotEnCours, setDepotEnCours] = useState(null);
  const [erreurDepot, setErreurDepot] = useState(null);
  const [onglet, setOnglet] = useState('remplir');

  useEffect(() => {
    if (!token) return;
    let annule = false;

    setLoading(true);
    setError(null);

    getDevisParToken(token)
      .then((res) => {
        if (annule) return;
        setDevis(res.data);
        // Un devis déjà signé s'ouvre sur les pièces : c'est ce qui lui reste.
        setOnglet(res.data.signature_le ? 'fichiers' : 'remplir');
        // Pré-remplissage avec ce que le back-office connaît déjà : le client
        // n'a plus qu'à compléter les champs restés vides.
        setInfos(
          Object.fromEntries(CHAMPS_CLIENT.map((c) => [c.cle, res.data[c.cle] || '']))
        );
        // Un échec ici ne prive pas de la signature : la section reste vide.
        getDocumentsParToken(token)
          .then((r) => {
            if (!annule) setDocuments(r.data);
          })
          .catch(() => {});
      })
      .catch((err) => {
        if (!annule) setError(err.message || 'Devis introuvable.');
      })
      .finally(() => {
        if (!annule) setLoading(false);
      });

    return () => {
      annule = true;
    };
  }, [token]);

  async function validerSignature(imageDataUrl) {
    setValidation(true);
    setErreurSignature(null);
    try {
      const res = await signerDevis(token, imageDataUrl, infos);
      setDevis(res.data);
      if (documents && documents.attendus.length > 0) setOnglet('fichiers');
    } catch (err) {
      setErreurSignature(err.message || 'La signature n’a pas pu être enregistrée.');
    } finally {
      setValidation(false);
    }
  }

  async function deposerDocument(attendu, fichier) {
    if (!fichier) return;
    setDepotEnCours(attendu.id);
    setErreurDepot(null);
    try {
      const res = await deposerDocumentParToken(token, fichier, { formationDocumentId: attendu.id });
      setDocuments(res.data);
    } catch (err) {
      setErreurDepot({ id: attendu.id, message: err.message || 'Dépôt impossible.' });
    } finally {
      setDepotEnCours(null);
    }
  }

  async function telechargerPdf() {
    setTelechargement(true);
    try {
      await telechargerPdfDevis(devis.id, { forcer: true, token });
    } catch (err) {
      setErreurSignature(err.message || 'Téléchargement impossible.');
    } finally {
      setTelechargement(false);
    }
  }

  const signe = Boolean(devis?.signature_le);
  const nbAttendus = documents?.attendus.length || 0;
  const nbRecus = documents?.attendus.filter((a) => a.depot).length || 0;
  // Signé : le PDF se régénère avec la signature ; avant, celui qui a été envoyé.
  const urlApercu = devis ? urlPdfDevis(devis.id, { apercu: true, token, forcer: signe }) : null;

  const ONGLETS = [
    { cle: 'remplir', label: signe ? 'Vos informations' : 'Remplir' },
    { cle: 'signer', label: signe ? 'Signature' : 'Signer', fait: signe },
    {
      cle: 'fichiers',
      label: 'Fichiers à téléverser',
      compte: nbAttendus ? `${nbRecus} / ${nbAttendus}` : null,
      alerte: nbAttendus > 0 && nbRecus < nbAttendus,
    },
  ];

  return (
    <div className="devis-page devis-page-scroll signature-doc">
      {token ? (
        <EnteteSignature
          titre={devis ? `Devis ${devis.numero}` : 'Signature du devis'}
          sousTitre={
            devis
              ? `${devis.client_societe || ''}${devis.date_emission ? ` · émis le ${formatDate(devis.date_emission)}` : ''}`
              : null
          }
          retour={retourListe ? () => navigate(retourListe) : null}
        />
      ) : (
        <div className="devis-header">
          <div>
            <h1 className="devis-title">Signature</h1>
            {/* Sans devis chargé, la page est ouverte depuis la sidebar, en
                interne : le sous-titre ne doit pas s'adresser au client, qui
                n'arrive ici que par le lien à jeton reçu par email. */}
            <p className="devis-subtitle">
              Choisissez le devis à faire signer par un client présent.
            </p>
          </div>
        </div>
      )}

      {!token && (
        <SelecteurSignature
          charger={getDevisASigner}
          colonnes={COLONNES_DEVIS}
          placeholder="Rechercher un numéro, un client..."
          compter={(n) => `${n} devis`}
          estSigne={(d) => Boolean(d.signature_le)}
          vide={{
            titre: 'Aucun devis à faire signer sur cette période.',
            sousTitre:
              "Un devis apparaît ici dès qu'il est validé, qu'il ait été envoyé ou non.",
          }}
          onChoisir={async (d) => {
            const res = await ouvrirSignatureDevis(d.id);
            navigate(`/signature-electronique/${res.data.token}`, {
              state: { retourListe: '/signature-electronique' },
            });
          }}
        />
      )}
      {token && loading && <div className="signature-etat">Chargement du devis...</div>}
      {token && !loading && error && (
        <div className="signature-etat signature-etat-erreur">{error}</div>
      )}

      {token && !loading && !error && devis && (
        <>
          {/* Le document d'abord, tel qu'il partira ou tel qu'il a été signé. */}
          <ApercuDocument url={urlApercu} titre={`Devis ${devis.numero}`} />

          {signe && (
            <div className="signature-etat signature-etat-succes">
              <p>Devis signé le {formatDateTime(devis.signature_le)}.</p>
              <div className="signature-etat-succes-actions">
                <button
                  type="button"
                  className="devis-btn"
                  onClick={telechargerPdf}
                  disabled={telechargement}
                >
                  {telechargement ? 'Téléchargement' : 'Télécharger le PDF signé'}
                </button>
              </div>
            </div>
          )}

          <nav className="clients-onglets signature-onglets" aria-label="Étapes">
            {ONGLETS.map((o) => (
              <button
                key={o.cle}
                type="button"
                className={'clients-onglet' + (onglet === o.cle ? ' clients-onglet-actif' : '')}
                onClick={() => setOnglet(o.cle)}
              >
                {o.label}
                {o.fait && <span className="signature-onglet-fait" aria-label="fait">✓</span>}
                {o.compte && (
                  <span className={'clients-onglet-compte' + (o.alerte ? ' clients-onglet-compte-alerte' : '')}>
                    {o.compte}
                  </span>
                )}
              </button>
            ))}
          </nav>

          {/* ---------------- Remplir ---------------- */}
          {onglet === 'remplir' && (
            <div className="signature-section">
              <div className="devis-carte signature-carte">
                <div className="devis-carte-titre">INFORMATIONS CLIENT</div>
                <dl>
                  <dt>Société</dt>
                  <dd>{devis.client_societe || '—'}</dd>
                  <dt>Contact</dt>
                  <dd>{[devis.prenom, devis.nom].filter(Boolean).join(' ') || '—'}</dd>
                </dl>

                {signe ? (
                  // Devis signé : les informations sont figées avec lui.
                  <dl>
                    {CHAMPS_CLIENT.map(({ cle, label }) => (
                      <Fragment key={cle}>
                        <dt>{label}</dt>
                        <dd>{devis[cle] || '—'}</dd>
                      </Fragment>
                    ))}
                  </dl>
                ) : (
                  <div className="signature-infos">
                    <p className="signature-infos-aide">
                      Merci de vérifier et de compléter les informations de votre entreprise
                      avant de signer.
                    </p>
                    {CHAMPS_CLIENT.map(({ cle, label, placeholder, inputMode, pleineLargeur }) => (
                      <label
                        className={`signature-champ${pleineLargeur ? ' signature-champ-large' : ''}`}
                        key={cle}
                      >
                        <span className="signature-champ-label">{label}</span>
                        <input
                          type="text"
                          className="signature-champ-input"
                          value={infos[cle]}
                          placeholder={placeholder}
                          inputMode={inputMode}
                          autoComplete="off"
                          disabled={validation}
                          onChange={(e) =>
                            setInfos((prec) => ({ ...prec, [cle]: e.target.value }))
                          }
                        />
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {devis.resume_echange && (
                <>
                  <div className="devis-form-section-title">Résumé de l&apos;échange</div>
                  <div className="devis-resume">{devis.resume_echange}</div>
                </>
              )}

              {!signe && (
                <div className="signature-section-suite">
                  <button
                    type="button"
                    className="devis-btn devis-btn-primary"
                    onClick={() => setOnglet('signer')}
                  >
                    Passer à la signature →
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ---------------- Signer ---------------- */}
          {onglet === 'signer' && (
            <div className="signature-section">
              {signe ? (
                <div className="signature-etat">
                  Ce devis est signé, merci ! Les pièces demandées se déposent dans l’onglet
                  « Fichiers à téléverser ».
                </div>
              ) : (
                <>
                  <p className="signature-mention">
                    En signant ci-dessous, vous acceptez le devis pour le compte de{' '}
                    <strong>{devis.client_societe || 'votre entreprise'}</strong>, avec les
                    informations saisies.
                  </p>
                  {erreurSignature && (
                    <div className="signature-etat signature-etat-erreur">{erreurSignature}</div>
                  )}
                  <SignaturePad onValider={validerSignature} validation={validation} />
                </>
              )}
            </div>
          )}

          {/* ---------------- Fichiers à téléverser ---------------- */}
          {onglet === 'fichiers' && (
            <div className="signature-section">
              {!documents && (
                <div className="signature-etat">Chargement des pièces demandées...</div>
              )}
              {documents && documents.attendus.length === 0 && (
                <div className="signature-etat">
                  Aucune pièce n&apos;est à fournir pour ce devis.
                </div>
              )}
              {documents && documents.attendus.length > 0 && (
                <>
                  <p className="signature-infos-aide">
                    Merci de déposer ces pièces, maintenant ou plus tard via ce même lien
                    {signe ? '' : ' ; vous pouvez signer sans attendre'}.
                  </p>
                  <ul className="signature-documents">
                    {documents.attendus.map((a) => (
                      <li className="signature-document" key={a.id}>
                        <div className="signature-document-texte">
                          <div className="signature-document-libelle">
                            {a.libelle}
                            {!a.obligatoire && (
                              <span className="signature-document-facultatif"> (facultatif)</span>
                            )}
                          </div>
                          <div className="signature-document-detail">
                            {a.formationIntitule}
                            {a.description ? ` · ${a.description}` : ''}
                          </div>
                          {erreurDepot?.id === a.id && (
                            <div className="signature-document-erreur">{erreurDepot.message}</div>
                          )}
                        </div>
                        {a.depot ? (
                          <div className="signature-document-recu">
                            Reçu : {a.depot.nomFichier}
                            <div className="signature-document-detail">
                              le {formatDateTime(a.depot.deposeLe)}
                            </div>
                            <label className="signature-document-remplacer">
                              Remplacer
                              <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
                                disabled={depotEnCours === a.id}
                                onChange={(e) => deposerDocument(a, e.target.files?.[0])}
                              />
                            </label>
                          </div>
                        ) : (
                          <label className="signature-document-depot devis-btn devis-btn-primary">
                            {depotEnCours === a.id ? 'Envoi' : 'Déposer le fichier'}
                            <input
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
                              disabled={depotEnCours === a.id}
                              onChange={(e) => deposerDocument(a, e.target.files?.[0])}
                            />
                          </label>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Signature;
